import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { ref, onValue } from 'firebase/database';
import { FaTimes, FaMapMarkerAlt, FaCalendarAlt, FaUtensils, FaHotel, FaBus, FaBinoculars, FaClock } from 'react-icons/fa';

const DomesticTours = () => {
  const [tours, setTours] = useState([]);
  const [selectedTour, setSelectedTour] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const tourRef = ref(db, 'tours');
    onValue(tourRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const domesticTours = Object.entries(data)
          .map(([id, tour]) => ({ id, ...tour }))
          .filter(tour => tour.category?.toLowerCase() === 'domestic-tour');
        setTours(domesticTours);
      }
      setLoading(false);
    });
  }, []);

  const openTourModal = (tour) => {
    setSelectedTour(tour);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTour(null);
    document.body.style.overflow = 'auto';
  };

  // Updated helper function to format duration properly from Firebase data
  const formatDuration = (nights, days) => {
    // If we have both nights and days from Firebase, use them directly
    if (nights !== undefined && days !== undefined) {
      const nightsNum = parseInt(nights, 10) || 0;
      const daysNum = parseInt(days, 10) || 1;
      return `${nightsNum} Nights ${daysNum} Days`;
    }
    
    // Fallback for old data format (if duration field exists)
    if (nights && typeof nights === 'string') {
      const daysFromString = parseInt(nights, 10) || 5;
      const nightsFromString = Math.max(0, daysFromString - 1);
      return `${nightsFromString} Nights ${daysFromString} Days`;
    }
    
    // Default fallback
    return '4 Nights 5 Days';
  };

  if (loading) {
    return (
      <div className="domestic-loading">
        <div className="domestic-spinner"><div></div></div>
        <p>Loading Domestic Tours...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* All your existing CSS styles remain the same */
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        /* Loading Styles */
        .domestic-loading {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 60px 20px;
          font-family: 'Poppins', sans-serif;
          font-weight: 500;
          font-size: 1.1rem;
          color: #2193b0;
          min-height: 60vh;
        }

        .domestic-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid rgba(33, 147, 176, 0.2);
          border-top: 4px solid #2193b0;
          border-radius: 50%;
          animation: domesticSpin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes domesticSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Main Container */
        .domestic-tours-container {
          padding: 40px 20px;
          background: #f8f9fa;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        /* Tours Grid - Perfect alignment and consistent sizing */
        .domestic-tours-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
          gap: 24px;
          max-width: 1400px;
          width: 100%;
          margin: 0 auto;
          padding: 0 10px;
          justify-items: center;
          align-items: stretch;
        }

        /* Tour Card - FIXED UNIFORM DIMENSIONS */
        .domestic-tour-card {
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 8px 32px rgba(33, 147, 176, 0.12);
          overflow: hidden;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
          display: flex;
          flex-direction: column;
          border: 2px solid transparent;
          animation: domesticCardFadeIn 0.6s ease forwards;
          opacity: 0;
          transform: translateY(30px);
          /* CONSISTENT FIXED DIMENSIONS FOR ALL CARDS */
          width: 100%;
          max-width: 380px;
          height: 540px; /* Fixed height for perfect uniformity */
          min-height: 540px;
        }

        .domestic-tour-card:nth-child(1) { animation-delay: 0.1s; }
        .domestic-tour-card:nth-child(2) { animation-delay: 0.2s; }
        .domestic-tour-card:nth-child(3) { animation-delay: 0.3s; }
        .domestic-tour-card:nth-child(4) { animation-delay: 0.4s; }
        .domestic-tour-card:nth-child(5) { animation-delay: 0.5s; }
        .domestic-tour-card:nth-child(6) { animation-delay: 0.6s; }

        @keyframes domesticCardFadeIn {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .domestic-tour-card:hover {
          transform: translateY(-12px) scale(1.02);
          box-shadow: 0 20px 40px rgba(33, 147, 176, 0.25);
          border-color: rgba(33, 147, 176, 0.3);
        }

        /* Fixed image dimensions - Same for all cards */
        .domestic-tour-image {
          width: 100%;
          height: 240px; /* Fixed height */
          object-fit: cover;
          object-position: center;
          transition: transform 0.4s ease;
          display: block;
          background: #f0f4f8 url('https://images.unsplash.com/photo-1586016413664-864c0dd76f53?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80') center center/cover no-repeat;
          flex-shrink: 0;
        }

        .domestic-tour-card:hover .domestic-tour-image {
          transform: scale(1.08);
        }

        /* Card content with EXACT spacing for uniformity */
        .domestic-card-content {
          padding: 24px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          height: 300px; /* Fixed content area height */
          flex: 1;
        }

        .domestic-tour-title {
          font-family: 'Poppins', sans-serif;
          font-size: 1.25rem;
          font-weight: 700;
          color: #0a0808;
          margin-bottom: 12px;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          height: 3.2em; /* Fixed title height */
          flex-shrink: 0;
        }

        .domestic-tour-duration {
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          font-size: 0.9rem;
          color: #050505;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          background: rgba(33, 147, 176, 0.1);
          padding: 8px 12px;
          border-radius: 12px;
          width: fit-content;
          height: 36px; /* Fixed duration height */
          flex-shrink: 0;
        }

        .domestic-duration-icon {
          margin-right: 8px;
          color: rgba(0, 0, 0, 0.7);
          font-size: 0.85rem;
        }

        /* Feature Tags Container with EXACT grid sizing */
        .domestic-tour-features {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          margin-bottom: 20px;
          width: 100%;
          height: 80px; /* Fixed features area height */
          flex-shrink: 0;
        }

        .domestic-feature-tag {
          background: linear-gradient(135deg, #1a62cf, #266996);
          color: white;
          padding: 10px 12px;
          border-radius: 20px;
          font-family: 'Poppins', sans-serif;
          font-size: 0.75rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          white-space: nowrap;
          box-shadow: 0 3px 10px rgba(26, 98, 207, 0.3);
          transition: all 0.3s ease;
          border: 1px solid rgba(255, 255, 255, 0.2);
          position: relative;
          overflow: hidden;
          height: 36px; /* Fixed tag height */
        }

        .domestic-feature-tag::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s ease;
        }

        .domestic-feature-tag:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(26, 98, 207, 0.4);
        }

        .domestic-feature-tag:hover::before {
          left: 100%;
        }

        .domestic-feature-tag .icon {
          margin-right: 6px;
          font-size: 0.7rem;
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
        }

        .domestic-view-button {
          align-self: center;
          background: linear-gradient(135deg, #023e8a, #0077b6);
          color: white;
          padding: 12px 24px;
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          font-size: 0.95rem;
          border: none;
          border-radius: 30px;
          cursor: pointer;
          box-shadow: 0 8px 24px rgba(33, 147, 176, 0.3);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          width: fit-content;
          min-width: 140px;
          height: 44px; /* Fixed button height */
          flex-shrink: 0;
          margin-top: auto;
        }

        .domestic-view-button::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          transition: all 0.6s ease;
          transform: translate(-50%, -50%);
        }

        .domestic-view-button:hover::before {
          width: 200px;
          height: 200px;
        }

        .domestic-view-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 16px 32px rgba(10, 147, 150, 0.4);
        }

        /* No Tours Message */
        .domestic-no-tours {
          text-align: center;
          padding: 50px 30px;
          max-width: 600px;
          margin: 40px auto;
          background: white;
          border-radius: 20px;
          box-shadow: 0 12px 32px rgba(33, 147, 176, 0.15);
          font-family: 'Poppins', sans-serif;
        }

        .domestic-no-tours-icon {
          font-size: 3rem;
          color: #2193b0;
          margin-bottom: 20px;
        }

        .domestic-no-tours-title {
          font-family: 'Poppins', sans-serif;
          font-size: 1.8rem;
          color: #0a0808;
          margin-bottom: 16px;
          font-weight: 700;
        }

        .domestic-no-tours-text {
          font-family: 'Poppins', sans-serif;
          font-size: 1rem;
          color: #666;
          margin-bottom: 24px;
          line-height: 1.6;
        }

        .domestic-contact-link {
          display: inline-block;
          background: linear-gradient(135deg, #2193b0, #6dd5ed);
          color: white;
          padding: 12px 24px;
          border-radius: 30px;
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          font-size: 0.95rem;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .domestic-contact-link:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 28px rgba(33, 147, 176, 0.3);
        }

        /* Modal Styles */
        .domestic-modal-overlay {
          position: fixed;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.8);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          padding: 20px;
          backdrop-filter: blur(8px);
          animation: domesticOverlayFadeIn 0.3s ease;
        }

        @keyframes domesticOverlayFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .domestic-modal {
          background: white;
          border-radius: 20px;
          width: 100%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px rgba(33, 147, 176, 0.3);
          animation: domesticModalSlideIn 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
          display: flex;
          flex-direction: column;
        }

        @keyframes domesticModalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(30px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .domestic-modal-header {
          position: relative;
          overflow: hidden;
        }

        .domestic-modal-image {
          width: 100%;
          height: 300px;
          object-fit: cover;
          object-position: center;
          border-radius: 20px 20px 0 0;
          display: block;
          background: #f0f4f8 url('https://images.unsplash.com/photo-1586016413664-864c0dd76f53?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80') center center/cover no-repeat;
        }

        .domestic-close-button {
          position: absolute;
          top: 16px;
          right: 16px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          font-size: 1.1rem;
          cursor: pointer;
          display: flex;
          justify-content: center;
          align-items: center;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }

        .domestic-close-button:hover {
          background: rgba(0, 0, 0, 0.9);
          transform: rotate(90deg) scale(1.1);
        }

        .domestic-modal-body {
          padding: 32px;
          display: flex;
          flex-direction: column;
          font-family: 'Poppins', sans-serif;
        }

        .domestic-modal-title {
          font-family: 'Poppins', sans-serif;
          font-size: 2rem;
          color: #0a0808;
          font-weight: 700;
          margin-bottom: 16px;
          line-height: 1.2;
        }

        .domestic-modal-subtitle {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          color: #0a0808;
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          margin-bottom: 24px;
          font-size: 1rem;
        }

        .domestic-modal-subtitle span {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .domestic-modal-description {
          color: #0a0808;
          font-family: 'Poppins', sans-serif;
          font-size: 1rem;
          line-height: 1.7;
          margin-bottom: 32px;
        }

        .domestic-features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 16px;
        }

        .domestic-feature-card {
          background: linear-gradient(135deg, #e0f7fa, #f0f9ff);
          border-radius: 16px;
          padding: 20px;
          display: flex;
          gap: 16px;
          align-items: flex-start;
          border: 1px solid rgba(33, 147, 176, 0.1);
          transition: transform 0.3s ease;
        }

        .domestic-feature-card:hover {
          transform: translateY(-4px);
        }

        .domestic-feature-icon {
          color: #2193b0;
          font-size: 1.5rem;
          min-width: 32px;
          display: flex;
          justify-content: center;
          margin-top: 4px;
        }

        .domestic-feature-content h4 {
          margin-bottom: 8px;
          color: #0a0808;
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: 1rem;
        }

        .domestic-feature-content p {
          font-family: 'Poppins', sans-serif;
          font-size: 0.9rem;
          color: #0a0808;
          line-height: 1.5;
        }

        /* Scrollbar */
        .domestic-modal::-webkit-scrollbar {
          width: 8px;
        }

        .domestic-modal::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .domestic-modal::-webkit-scrollbar-thumb {
          background: #2193b0;
          border-radius: 4px;
        }

        .domestic-modal::-webkit-scrollbar-thumb:hover {
          background: #1a7a8a;
        }

        /* RESPONSIVE BREAKPOINTS - Maintaining card uniformity */

        /* Extra Large Devices (1400px+) */
        @media (min-width: 1400px) {
          .domestic-tours-grid {
            grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
            gap: 28px;
            max-width: 1600px;
          }
          
          .domestic-tour-card {
            max-width: 400px;
            height: 580px;
            min-height: 580px;
          }
          
          .domestic-tour-image {
            height: 280px;
          }
          
          .domestic-card-content {
            height: 300px;
          }
        }

        /* Large Devices (1200px) */
        @media (max-width: 1200px) {
          .domestic-tours-grid {
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
            gap: 22px;
            max-width: 1200px;
          }
          
          .domestic-tour-card {
            max-width: 360px;
            height: 520px;
            min-height: 520px;
          }
          
          .domestic-tour-image {
            height: 240px;
          }
          
          .domestic-card-content {
            height: 280px;
          }
          
          .domestic-modal-image {
            height: 280px;
          }
          
          .domestic-modal-title {
            font-size: 1.8rem;
          }
        }

        /* Medium Devices (992px) */
        @media (max-width: 992px) {
          .domestic-tours-container {
            padding: 30px 15px;
          }
          
          .domestic-tours-grid {
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 20px;
          }
          
          .domestic-tour-card {
            max-width: 340px;
            height: 500px;
            min-height: 500px;
          }
          
          .domestic-tour-image {
            height: 220px;
          }
          
          .domestic-card-content {
            padding: 20px;
            height: 280px;
          }
          
          .domestic-modal {
            max-width: 700px;
          }
          
          .domestic-modal-image {
            height: 250px;
          }
          
          .domestic-modal-body {
            padding: 24px;
          }
          
          .domestic-modal-title {
            font-size: 1.6rem;
          }
          
          .domestic-features-grid {
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          }
        }

        /* Small Devices (768px) */
        @media (max-width: 768px) {
          .domestic-tours-container {
            padding: 20px 10px;
          }
          
          .domestic-tours-grid {
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 16px;
            padding: 0 5px;
          }
          
          .domestic-tour-card {
            max-width: 100%;
            height: 480px;
            min-height: 480px;
          }
          
          .domestic-tour-image {
            height: 200px;
          }
          
          .domestic-card-content {
            padding: 18px;
            height: 280px;
          }
          
          .domestic-tour-title {
            font-size: 1.2rem;
            height: 3em;
          }
          
          .domestic-modal {
            max-width: 95vw;
            margin: 10px;
          }
          
          .domestic-modal-image {
            height: 220px;
          }
          
          .domestic-modal-body {
            padding: 20px;
          }
          
          .domestic-modal-title {
            font-size: 1.4rem;
          }
          
          .domestic-modal-subtitle {
            flex-direction: column;
            gap: 12px;
          }
          
          .domestic-features-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          
          .domestic-feature-card {
            padding: 16px;
          }
          
          .domestic-no-tours {
            padding: 40px 20px;
            margin: 20px auto;
          }
          
          .domestic-no-tours-title {
            font-size: 1.5rem;
          }
        }

        /* Extra Small Devices (576px) */
        @media (max-width: 576px) {
          .domestic-tours-grid {
            grid-template-columns: 1fr;
            gap: 14px;
          }
          
          .domestic-tour-card {
            height: 460px;
            min-height: 460px;
            max-width: 100%;
          }
          
          .domestic-tour-image {
            height: 180px;
          }
          
          .domestic-card-content {
            padding: 16px;
            height: 280px;
          }
          
          .domestic-tour-title {
            font-size: 1.1rem;
            margin-bottom: 10px;
            height: 2.8em;
          }
          
          .domestic-tour-duration {
            font-size: 0.85rem;
            padding: 6px 10px;
            height: 32px;
          }
          
          .domestic-tour-features {
            gap: 6px;
            height: 74px;
          }
          
          .domestic-feature-tag {
            font-size: 0.7rem;
            padding: 8px 10px;
            height: 32px;
          }
          
          .domestic-feature-tag .icon {
            font-size: 0.65rem;
            margin-right: 4px;
          }
          
          .domestic-view-button {
            padding: 10px 20px;
            font-size: 0.9rem;
            min-width: 120px;
            height: 40px;
          }
          
          .domestic-modal-image {
            height: 200px;
          }
          
          .domestic-modal-body {
            padding: 16px;
          }
          
          .domestic-modal-title {
            font-size: 1.3rem;
          }
          
          .domestic-modal-subtitle {
            font-size: 0.9rem;
          }
          
          .domestic-modal-description {
            font-size: 0.9rem;
          }
          
          .domestic-feature-card {
            padding: 14px;
            gap: 12px;
          }
          
          .domestic-feature-icon {
            font-size: 1.3rem;
            min-width: 28px;
          }
          
          .domestic-close-button {
            width: 36px;
            height: 36px;
            font-size: 1rem;
          }
          
          .domestic-no-tours {
            padding: 30px 15px;
          }
          
          .domestic-no-tours-icon {
            font-size: 2.5rem;
          }
          
          .domestic-no-tours-title {
            font-size: 1.3rem;
          }
          
          .domestic-no-tours-text {
            font-size: 0.9rem;
          }
        }

        /* Ultra Small Devices (480px) */
        @media (max-width: 480px) {
          .domestic-tours-container {
            padding: 15px 5px;
          }
          
          .domestic-tours-grid {
            padding: 0;
            gap: 12px;
          }
          
          .domestic-tour-card {
            height: 420px;
            min-height: 420px;
          }
          
          .domestic-tour-image {
            height: 160px;
          }
          
          .domestic-card-content {
            padding: 14px;
            height: 260px;
          }
          
          .domestic-tour-title {
            font-size: 1rem;
            line-height: 1.2;
            height: 2.4em;
          }
          
          .domestic-tour-duration {
            font-size: 0.8rem;
            padding: 5px 8px;
            height: 30px;
          }
          
          .domestic-tour-features {
            gap: 5px;
            height: 66px;
          }
          
          .domestic-feature-tag {
            font-size: 0.68rem;
            padding: 6px 8px;
            border-radius: 16px;
            height: 30px;
          }
          
          .domestic-feature-tag .icon {
            font-size: 0.6rem;
            margin-right: 3px;
          }
          
          .domestic-view-button {
            padding: 8px 16px;
            font-size: 0.85rem;
            min-width: 100px;
            height: 36px;
          }
          
          .domestic-modal {
            margin: 5px;
            max-width: calc(100vw - 10px);
          }
          
          .domestic-modal-image {
            height: 180px;
          }
          
          .domestic-modal-body {
            padding: 14px;
          }
          
          .domestic-modal-title {
            font-size: 1.2rem;
          }
          
          .domestic-modal-subtitle {
            font-size: 0.85rem;
          }
          
          .domestic-modal-description {
            font-size: 0.85rem;
          }
          
          .domestic-feature-card {
            padding: 12px;
          }
          
          .domestic-feature-content h4 {
            font-size: 0.9rem;
          }
          
          .domestic-feature-content p {
            font-size: 0.8rem;
          }
          
          .domestic-close-button {
            width: 32px;
            height: 32px;
            font-size: 0.9rem;
            top: 12px;
            right: 12px;
          }
        }

        /* Ultra Ultra Small Devices (360px) */
        @media (max-width: 360px) {
          .domestic-tours-grid {
            gap: 10px;
          }
          
          .domestic-tour-card {
            height: 400px;
            min-height: 400px;
          }
          
          .domestic-tour-image {
            height: 150px;
          }
          
          .domestic-card-content {
            padding: 12px;
            height: 250px;
          }
          
          .domestic-tour-title {
            font-size: 0.95rem;
            height: 2.2em;
          }
          
          .domestic-tour-features {
            gap: 4px;
            height: 62px;
          }
          
          .domestic-feature-tag {
            font-size: 0.65rem;
            padding: 5px 7px;
            border-radius: 14px;
            height: 28px;
          }
          
          .domestic-feature-tag .icon {
            font-size: 0.58rem;
            margin-right: 2px;
          }
          
          .domestic-view-button {
            height: 34px;
          }
          
          .domestic-modal-image {
            height: 160px;
          }
          
          .domestic-modal-title {
            font-size: 1.1rem;
          }
          
          .domestic-feature-card {
            padding: 10px;
            gap: 10px;
          }
          
          .domestic-feature-icon {
            font-size: 1.2rem;
            min-width: 24px;
          }
          
          .domestic-no-tours {
            padding: 25px 12px;
          }
          
          .domestic-no-tours-icon {
            font-size: 2rem;
          }
          
          .domestic-no-tours-title {
            font-size: 1.2rem;
          }
        }
      `}</style>

      <div className="domestic-tours-container">
        {tours.length === 0 ? (
          <div className="domestic-no-tours">
            <div className="domestic-no-tours-icon">
              <FaMapMarkerAlt />
            </div>
            <h3 className="domestic-no-tours-title">No Domestic Tours Available</h3>
            <p className="domestic-no-tours-text">
              We're currently updating our domestic tour packages. Check back soon for exciting new destinations across India!
            </p>
            <a href="/contact-us" className="domestic-contact-link">
              Contact Us for Updates
            </a>
          </div>
        ) : (
          <div className="domestic-tours-grid">
            {tours.map((tour, index) => (
              <div
                className="domestic-tour-card"
                key={tour.id}
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => openTourModal(tour)}
              >
                <img
                  src={tour.image || 'https://images.unsplash.com/photo-1586016413664-864c0dd76f53?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'}
                  alt={tour.title || 'Domestic Tour'}
                  className="domestic-tour-image"
                  loading="lazy"
                />
                <div className="domestic-card-content">
                  <h3 className="domestic-tour-title">
                    {tour.title || 'Domestic Tour Package'}
                  </h3>
                  <div className="domestic-tour-duration">
                    <FaClock className="domestic-duration-icon" />
                    {formatDuration(tour.nights, tour.days)}
                  </div>
                  <div className="domestic-tour-features">
                    <span className="domestic-feature-tag">
                      <FaUtensils className="icon" />
                      Meals
                    </span>
                    <span className="domestic-feature-tag">
                      <FaHotel className="icon" />
                      Stay
                    </span>
                    <span className="domestic-feature-tag">
                      <FaBus className="icon" />
                      Transport
                    </span>
                    <span className="domestic-feature-tag">
                      <FaBinoculars className="icon" />
                      Sightseeing
                    </span>
                  </div>
                  <button className="domestic-view-button">
                    <span style={{ position: 'relative', zIndex: 1 }}>
                      View Details
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tour Details Modal */}
        {isModalOpen && selectedTour && (
          <div className="domestic-modal-overlay" onClick={closeModal}>
            <div className="domestic-modal" onClick={(e) => e.stopPropagation()}>
              <div className="domestic-modal-header">
                <img
                  src={selectedTour.image || 'https://images.unsplash.com/photo-1586016413664-864c0dd76f53?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'}
                  alt={selectedTour.title || 'Domestic Tour'}
                  className="domestic-modal-image"
                  loading="lazy"
                />
                <button onClick={closeModal} className="domestic-close-button">
                  <FaTimes />
                </button>
              </div>
              <div className="domestic-modal-body">
                <h2 className="domestic-modal-title">{selectedTour.title}</h2>
                <div className="domestic-modal-subtitle">
                  <span>
                    <FaMapMarkerAlt />
                    {selectedTour.destination || 'Multiple Destinations'}
                  </span>
                  <span>
                    <FaCalendarAlt />
                    {formatDuration(selectedTour.nights, selectedTour.days)}
                  </span>
                </div>
                <p className="domestic-modal-description">
                  {selectedTour.description || 'This incredible domestic tour package takes you through some of the most beautiful destinations in India. Experience diverse cultures, breathtaking landscapes, and unforgettable memories with our expertly planned itinerary.'}
                </p>
                <div className="domestic-features-grid">
                  <div className="domestic-feature-card">
                    <div className="domestic-feature-icon">
                      <FaUtensils />
                    </div>
                    <div className="domestic-feature-content">
                      <h4>Meals</h4>
                      <p>{selectedTour.meals || 'Daily breakfast and dinner included with local cuisine'}</p>
                    </div>
                  </div>
                  <div className="domestic-feature-card">
                    <div className="domestic-feature-icon">
                      <FaHotel />
                    </div>
                    <div className="domestic-feature-content">
                      <h4>Accommodation</h4>
                      <p>{selectedTour.accommodation || '3-star hotels with premium amenities'}</p>
                    </div>
                  </div>
                  <div className="domestic-feature-card">
                    <div className="domestic-feature-icon">
                      <FaBus />
                    </div>
                    <div className="domestic-feature-content">
                      <h4>Transportation</h4>
                      <p>{selectedTour.transports || 'Private air-conditioned vehicles for all transfers'}</p>
                    </div>
                  </div>
                  <div className="domestic-feature-card">
                    <div className="domestic-feature-icon">
                      <FaBinoculars />
                    </div>
                    <div className="domestic-feature-content">
                      <h4>Sightseeing</h4>
                      <p>{selectedTour.sightseeing || 'All major attractions with expert local guides'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DomesticTours;
